<template>
  <header v-if="section" class="mp-section-head" :class="{ 'no-tabs': !section.tabs?.length }">
    <div class="section-top">
      <div class="section-title">
        <h1>{{ section.label }}</h1>
        <p v-if="desc">{{ desc }}</p>
      </div>
      <div v-if="$slots.actions" class="section-actions">
        <slot name="actions" />
      </div>
    </div>
    <nav v-if="section.tabs?.length" class="section-tabs" role="tablist">
      <router-link
        v-for="tab in section.tabs"
        :key="tab.to"
        :to="tab.to"
        class="stab"
        :class="{ active: tab === activeTab }"
        role="tab"
      >
        {{ tab.label }}
      </router-link>
    </nav>
  </header>
</template>

<script>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { activeTabForPath, sectionByKey } from '../../config/navigation'

/** 板块页头：板块名 + 当前子页说明 + 子页标签 + 右侧操作。所有页面共用，页面内不再自带大标题。 */
export default {
  name: 'SectionHeader',
  props: {
    sectionKey: { type: String, required: true },
    // 覆盖导航里的静态说明（页面需要显示动态范围时用）
    desc: { type: String, default: '' },
  },
  setup(props) {
    const route = useRoute()
    const section = computed(() => sectionByKey(props.sectionKey))
    const activeTab = computed(() => activeTabForPath(section.value, route.path))
    const desc = computed(
      () => props.desc || activeTab.value?.desc || section.value?.desc || '',
    )
    return { section, activeTab, desc }
  },
}
</script>

<style scoped>
.mp-section-head {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 18px;
  border-bottom: 1px solid var(--mp-border);
}

.mp-section-head.no-tabs {
  padding-bottom: 14px;
}

.section-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
  flex-wrap: wrap;
}

.section-title {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 12px;
  min-width: 0;
}

.section-title h1 {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.3px;
  color: var(--mp-text);
}

.section-title p {
  margin: 0;
  font-size: 12px;
  color: var(--mp-text-muted);
}

.section-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.section-actions :deep(.el-button + .el-button) {
  margin-left: 0;
}

.section-tabs {
  display: flex;
  gap: 22px;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.section-tabs::-webkit-scrollbar {
  display: none;
}

.stab {
  position: relative;
  flex: 0 0 auto;
  padding: 2px 0 11px;
  font-size: 13px;
  font-weight: 650;
  color: var(--mp-text-secondary);
  text-decoration: none;
  white-space: nowrap;
}

.stab:hover {
  color: var(--mp-text);
}

.stab.active {
  color: var(--mp-accent);
}

.stab.active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 2px;
  border-radius: 2px;
  background: var(--mp-accent);
}

@media (max-width: 768px) {
  .mp-section-head {
    gap: 8px;
    margin-bottom: 12px;
  }

  .section-title h1 {
    font-size: 17px;
  }

  .section-title p {
    font-size: 11px;
  }

  .section-tabs {
    gap: 18px;
  }
}
</style>
